import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import landingImage from "../assets/landing.png";

// global chat widget available on every non-admin page
export default function ChatWidget() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]); // chat history
  const [bookingStep, setBookingStep] = useState(0);
  const [bookingInfo, setBookingInfo] = useState({
    name: "",
    email: "",
    phone: "",
    projectTitle: "",
    date: "",
    slot: "",
    notes: ""
  });
  const [awaitingBookingConfirm, setAwaitingBookingConfirm] = useState(false);
  const [showTip, setShowTip] = useState(true);

  useEffect(() => {
    // hide tip after a few seconds or when chat opens
    const timer = setTimeout(() => setShowTip(false), 8000);
    return () => clearTimeout(timer);
  }, []);

  // send welcome message once when chat opens
  useEffect(() => {
    if (isChatOpen && messages.length === 0) {
      setMessages([{ sender: "bot", text: "Hi! I'm Enzo, your friendly assistant. How can I help you today?" }]);
    }
  }, [isChatOpen]);
  const [inputValue, setInputValue] = useState("");
  const scrollRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // rule-based fallback used when the AI service is not available
  function generateFallback(text) {
    const msg = text.toLowerCase();
    if (msg.match(/\bhi\b|\bhello\b|\bhey\b/)) {
      return "Hello there! I'm Enzo — happy to chat with you. What would you like to know about?";
    }
    if (msg.includes("service")) {
      return (
        "We offer project guidance & mentoring, startup consultation, robotics consultation, and custom product development."
      );
    }
    if (msg.includes("about")) {
      return (
        "We empower innovations with cutting-edge technology products and training courses. Join us to shape the future together."
      );
    }
    if (msg.includes("contact")) {
      return (
        <div className="space-y-1 text-left">
          <div>You can reach us through the following channels:</div>
          <div>
            📞 <a href="tel:+918848295437" className="underline">+91 88482 95437</a>
          </div>
          <div>
            📷 <a href="https://www.instagram.com/ente.ai/" target="_blank" rel="noopener noreferrer" className="underline">
              Instagram
            </a>
          </div>
          <div>
            💬 <a href="https://wa.me/918848295437" target="_blank" rel="noopener noreferrer" className="underline">
              WhatsApp
            </a>
          </div>
          <div>
            ✉️ <a href="mailto:ente.ai.in@gmail.com" className="underline">
              ente.ai.in@gmail.com
            </a>
          </div>
        </div>
      );
    }
    if (msg.includes("book")) {
      if (!awaitingBookingConfirm) {
        setAwaitingBookingConfirm(true);
        return "Would you like me to help book a slot for you? (yes/no)";
      }
      // otherwise fall through and let handleSend deal with confirmation response
    }
    if (msg.includes("location") || msg.includes("where")) {
      // return embed iframe similar to contact page along with an open-in-Google-Maps button
      return (
        <div>
          <iframe
            title="Google Map Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3917.5032355722087!2d75.91026897504402!3d10.925307589232652!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba7b3f2220fd9d3%3A0x941ef169a0ba2a91!2sENTE%20AI!5e0!3m2!1sen!2sin!4v1756606803748!5m2!1sen!2sin"
            width="100%"
            height="250"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="grayscale hover:grayscale-0 transition-all duration-500"
          ></iframe>
          <div className="text-center mt-2">
            <a
              href="https://www.google.com/maps/search/?api=1&query=10.9253076,75.910269"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
            >
              Open in Google Maps
            </a>
          </div>
        </div>
      );
    }
    if (msg.includes("platform") || msg.includes("learning") || msg.includes("portal") || msg.includes("login") || msg.includes("python") || msg.includes("learn") || msg.includes("course") || msg.includes("class") || msg.includes("training")) {
      return (
        <div className="space-y-2 text-left">
          <div>
            You can access our Python programming course, comprehensive digital library, track your progress, and join interactive coding sessions on our dedicated learning platform!
          </div>
          <div>
            <a
              href="https://learning.enteai.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#D1D1D1] text-[#003049] font-bold px-4 py-2 rounded hover:bg-white transition-all shadow-md"
            >
              Launch Platform →
            </a>
          </div>
        </div>
      );
    }
    if (msg.includes("workshop")) {
      return (
        "We conduct robotics and AI workshops; visit the 'Courses' page to learn what's currently available."
      );
    }
    if (msg.includes("gallery")) {
      return (
        "You can view images of our activities in the Gallery page on the site."
      );
    }
    return (
      "Sorry, I didn't quite catch that. Try asking about our services, products, our environment, or how to contact us."
    );
  }

  const [isTyping, setIsTyping] = useState(false);

  // hits the OpenAI API if key is present, otherwise falls back
  async function fetchBotReply(userInput) {
    const apiKey = process.env.REACT_APP_OPENAI_KEY;
    if (!apiKey) {
      return generateFallback(userInput);
    }
    try {
      const resp = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [
            { role: "system", content: "You are Enzo, a helpful assistant for the EnteAI website. Provide accurate answers about our services, products, location, contact info, etc. If asked about learning, python, courses, portal, or platform, guide them to our learning platform at https://learning.enteai.in" },
            { role: "user", content: userInput },
          ],
          max_tokens: 150,
        }),
      });
      const data = await resp.json();
      return data.choices?.[0]?.message?.content || generateFallback(userInput);
    } catch (err) {
      console.error("OpenAI error", err);
      return generateFallback(userInput);
    }
  }

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    const text = inputValue.trim();
    const userMsg = { sender: "user", text, time: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue("");

    // handle initial booking confirmation prompt
    if (awaitingBookingConfirm) {
      let botReply = "";
      if (text.toLowerCase().startsWith("y")) {
        botReply = "Great! What's your full name?";
        setBookingStep(1);
      } else {
        botReply = "No problem—let me know if you change your mind.";
      }
      setAwaitingBookingConfirm(false);
      setMessages(prev => [...prev, { sender: "bot", text: botReply, time: new Date() }]);
      return;
    }

    // booking multi-step conversation
    if (bookingStep > 0) {
      const info = { ...bookingInfo };
      let botReply = "";
      switch (bookingStep) {
        case 1:
          info.name = text;
          botReply = "Thanks, and what's your email address?";
          setBookingStep(2);
          break;
        case 2:
          info.email = text;
          botReply = "Got it. Could you share your phone number?";
          setBookingStep(3);
          break;
        case 3:
          info.phone = text;
          botReply = "Great! What's the title of your project?";
          setBookingStep(4);
          break;
        case 4:
          info.projectTitle = text;
          botReply = "Please pick a preferred date (YYYY-MM-DD).";
          setBookingStep(5);
          break;
        case 5:
          info.date = text;
          botReply = "Which slot would you like, forenoon or afternoon?";
          setBookingStep(6);
          break;
        case 6:
          info.slot = text.toLowerCase().includes("forenoon") ? "forenoon" : "afternoon";
          botReply = "Any additional notes? (or type 'no')";
          setBookingStep(7);
          break;
        case 7:
          info.notes = text;
          botReply = `All set! Submitting your booking for ${info.date} (${info.slot}).`;
          setBookingStep(0);
          fetch(`${process.env.REACT_APP_API_BASE || ''}/api/book-slot`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(info),
          }).then(res => res.json()).then(d => console.log('booking resp', d)).catch(console.error);
          break;
      }
      setBookingInfo(info);
      setMessages(prev => [...prev, { sender: "bot", text: botReply, time: new Date() }]);
      return;
    }

    setIsTyping(true);
    const reply = await fetchBotReply(text);
    setIsTyping(false);
    const botMsg = { sender: "bot", text: reply, time: new Date() };
    setMessages(prev => [...prev, botMsg]);
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  // add welcome message when opened
  useEffect(() => {
    if (isChatOpen && messages.length === 0) {
      setMessages([{ sender: "bot", text: "Hello! I'm your friendly assistant. Ask me anything about the site.", time: new Date() }]);
    }
  }, [isChatOpen]);

  return (
    <>
      {/* Floating AI Assistant Button */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0, rotate: -20 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 1.5, type: "spring", stiffness: 260, damping: 20 }}
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 max-w-[4rem] max-h-[4rem]"
        >
          <motion.button
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => { setIsChatOpen(!isChatOpen); setShowTip(false); }}
            className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center group"
          >
            {showTip && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute -top-10 left-0 bg-[#002639]/90 text-white text-xs rounded-md px-2 py-1 shadow-md"
              >
                Hey, Enzo here
              </motion.div>
            )}
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-[#457B9D] rounded-full blur-xl opacity-40 group-hover:opacity-70 transition-opacity animate-pulse" />

              {/* Button Body */}
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src={landingImage}
                  alt="AI Assistant"
                  className="w-full h-full object-contain brightness-110 drop-shadow-2xl"
                />
              </div>

              {/* Notification Dot */}
              <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full border-2 border-[#003049] animate-bounce" />
            </motion.button>
        </motion.div>
      </AnimatePresence>

      {/* Chat Overlay Interface */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-4 right-4 sm:bottom-32 sm:right-8 z-50 w-full max-w-[350px] h-[60vh] sm:h-[500px] bg-[#002639]/95 backdrop-blur-xl border border-[#457B9D] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Chat Header */}
            <div className="p-4 border-b border-[#457B9D] bg-[#457B9D]/20 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="font-bold text-white">Enzo 🐾</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setMessages([])}
                  title="Clear chat"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {/* trash icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
                  </svg>
                </button>
                <button
                  onClick={() => setIsChatOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Chat Body */}
            <div 
              ref={scrollRef}
              className="flex-1 p-4 flex flex-col overflow-y-auto scroll-smooth" 
              style={{ maxHeight: "calc(100% - 120px)" }}
            >
              {messages.length === 0 && (
                <p className="text-gray-400 italic text-center">
                  How can I help you today?
                </p>
              )}
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={`mb-2 flex items-end ${m.sender === "bot" ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`px-3 py-2 rounded-lg max-w-[80%] text-sm leading-snug ${
                      m.sender === "bot" ? "bg-[#457B9D]/20 text-white" : "bg-[#D1D1D1] text-[#003049]"
                    }`}
                  >
                    {m.text}
                    <div className="text-[10px] text-gray-400 mt-1 text-right">
                      {m.time && new Date(m.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="mb-2 flex justify-start">
                  <div className="px-3 py-2 rounded-lg max-w-[80%] text-sm leading-snug bg-[#457B9D]/20 text-white">
                    Enzo is typing...
                  </div>
                </div>
              )}
            </div>
            {/* optional quick contact buttons */}
            {/* <div className="px-4 py-2 border-t border-[#457B9D] flex justify-around">
              <a href="tel:+918848295437" className="text-sm text-[#D1D1D1] hover:text-white flex items-center gap-1">
                📞 <span>Call</span>
              </a>
              <a href="https://www.instagram.com/ente.ai/" target="_blank" rel="noopener noreferrer" className="text-sm text-[#D1D1D1] hover:text-white flex items-center gap-1">
                📷 <span>Instagram</span>
              </a>
              <a href="https://wa.me/918848295437" target="_blank" rel="noopener noreferrer" className="text-sm text-[#D1D1D1] hover:text-white flex items-center gap-1">
                💬 <span>WhatsApp</span>
              </a>
            </div> */}
            {/* Chat Input */}
            <div className="p-4 border-t border-[#457B9D] relative">
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Enzo anything..."
                className="w-full bg-[#003049] border border-[#457B9D] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#D1D1D1] transition-colors text-sm"
                autoFocus
              />
              <button
                onClick={handleSend}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#D1D1D1] hover:text-white"
              >
                Send
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
